import camelcaseKeys from 'camelcase-keys';
import mitt from 'mitt';
import { WebSocket } from 'partysocket';
import snakecaseKeys from 'snakecase-keys';

import { ConnectionStatusController, observability } from '@/shared/lib';

import type { WsResponse, WsResponseWritable } from '../generated';

type Events = {
  [K in WsResponse as K['type']]: K['payload'];
};
type EventsWritable = {
  [K in WsResponseWritable as K['type']]: K['payload'];
};

const useWebSocketClient = () => {
  const emitter = mitt<Events>();
  const maxRetries = 8;

  const localWebSocketClient = new WebSocket(import.meta.env.WS_URL, [], {
    startClosed: true,
    debug: import.meta.env.DEV,
    minReconnectionDelay: 1_000 + Math.random() * 1_000,
    maxReconnectionDelay: 15_000,
    reconnectionDelayGrowFactor: 1.5,
    maxRetries,
    shouldReconnectOnClose: () => navigator.onLine,
  });

  localWebSocketClient.addEventListener('message', (event) => {
    try {
      const data: WsResponse = JSON.parse(event.data);
      emitter.emit(data.type, camelcaseKeys(data.payload, { deep: true }));
    } catch (e) {
      console.error('WS parsing error:', e);
      observability.captureException(e);
    }
  });

  localWebSocketClient.addEventListener('open', () => {
    ConnectionStatusController.setWebSocketConnected();

    observability.addBreadcrumb({
      category: 'websocket',
      message: 'connected',
      level: 'info',
    });
  });

  localWebSocketClient.addEventListener('close', (event) => {
    ConnectionStatusController.setWebSocketDisconnected();

    if (navigator.onLine && localWebSocketClient.retryCount >= maxRetries) {
      ConnectionStatusController.setWebSocketConnectionRequested(false);
    }

    observability.addBreadcrumb({
      category: 'websocket',
      message: 'closed',
      data: {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      },
      level: event.wasClean ? 'info' : 'warning',
    });

    if (!event.wasClean && event.code !== 1000) {
      observability.captureMessage('websocket-disconnected', {
        level: 'warning',
        tags: {
          code: String(event.code),
        },
        extra: {
          reason: event.reason,
        },
      });
    }
  });

  localWebSocketClient.addEventListener('error', () => {
    observability.captureMessage('websocket-error', {
      level: 'error',
    });
  });

  const WebSocketClient = {
    on: <K extends keyof Events>(event: K, handler: (data: Events[K]) => void) => {
      emitter.on(event, handler);
    },

    off: <K extends keyof Events>(event: K, handler: (data: Events[K]) => void) => {
      emitter.off(event, handler);
    },

    send: <T extends keyof EventsWritable>(type: T, payload: EventsWritable[T]) => {
      const message: WsResponseWritable = {
        type,
        payload,
        timestamp: new Date().toString(),
      } as WsResponseWritable;

      const messageAsString: string = JSON.stringify(snakecaseKeys(message, { deep: true }));
      localWebSocketClient.send(messageAsString);
    },
  } as const;

  const WebSocketClientController = {
    connect: () => {
      ConnectionStatusController.setWebSocketConnectionRequested(true);
      localWebSocketClient.reconnect();
    },
    close: () => {
      ConnectionStatusController.resetWebSocket();

      const state = localWebSocketClient.readyState;
      if (state === localWebSocketClient.CLOSED || state === localWebSocketClient.CLOSING) {
        return;
      }

      localWebSocketClient.close();
    },
  } as const;

  return { WebSocketClient, WebSocketClientController };
};

const { WebSocketClient, WebSocketClientController } = useWebSocketClient();

export { WebSocketClient, WebSocketClientController };
